import type { CreateTournamentInput, PaymentMethod, TeamFormation } from './types'

export type StepId = 'basics' | 'format' | 'entry' | 'schedule' | 'review'

export const STEPS: { id: StepId; label: string; title: string; intro: string }[] = [
  {
    id: 'basics',
    label: 'Game & basics',
    title: 'Game & basics',
    intro: 'Pick the game and give your tournament a name players will recognize.',
  },
  {
    id: 'format',
    label: 'Format',
    title: 'Format',
    intro: 'Decide how teams are made and how the bracket runs.',
  },
  {
    id: 'entry',
    label: 'Entry & prizes',
    title: 'Entry & prizes',
    intro: 'Choose how the prize pool is funded and how it’s split.',
  },
  {
    id: 'schedule',
    label: 'Schedule',
    title: 'Schedule',
    intro: 'Set when the tournament starts and ends.',
  },
  {
    id: 'review',
    label: 'Review',
    title: 'Review and create',
    intro: 'Check everything once. You can jump back to any step.',
  },
]

export const LAST_STEP = STEPS.length - 1

export type FieldName = keyof CreateTournamentInput

/** The form field that holds team size: team signup uses max_player_size, solo signup players_per_team (unchanged contract). */
export const teamSizeField = (formation: TeamFormation): 'max_player_size' | 'players_per_team' =>
  formation === 'teams' ? 'max_player_size' : 'players_per_team'

/**
 * Team size limits per formation. Solo-signup formats (Captain draft, Region teams)
 * bind to `players_per_team`, which the backend validates as 2–10 (422 otherwise).
 * Teams register keeps its 1–50 roster size.
 */
export const SOLO_TEAM_SIZE = { min: 2, max: 10 } as const
export const TEAMS_TEAM_SIZE = { min: 1, max: 50 } as const
export const teamSizeLimits = (formation: TeamFormation) =>
  formation === 'teams' ? TEAMS_TEAM_SIZE : SOLO_TEAM_SIZE

/** Validation message for a team size value, or null when it's valid. */
export const teamSizeError = (formation: TeamFormation, value: unknown): string | null => {
  const { min, max } = teamSizeLimits(formation)
  const raw = String(value ?? '').trim()
  const n = Number(raw)
  if (!raw || !Number.isInteger(n) || n < min || n > max) {
    return formation === 'teams'
      ? `Team size must be between ${min} and ${max} players.`
      : `Solo-signup teams need ${min}–${max} players each.`
  }
  return null
}

/** RHF field names validated by each step's Next button. */
export const stepFields = (stepIndex: number, formation: TeamFormation): FieldName[] => {
  switch (STEPS[stepIndex]?.id) {
    case 'basics':
      return ['game', 'tournament_name']
    case 'format':
      return formation === 'teams'
        ? ['play_format', 'number_of_teams', teamSizeField(formation)]
        : ['play_format', teamSizeField(formation)]
    case 'entry':
      return ['payment_method', 'prize_pool']
    case 'schedule':
      return ['startDate', 'endDate']
    default:
      return []
  }
}

/** Which step owns a field (used to jump to the first error on Create). */
export const stepOfField = (field: string): number => {
  if (['game', 'tournament_name', 'tournament_description', 'avatar', 'cover_photo'].includes(field)) return 0
  if (['play_format', 'number_of_teams', 'max_player_size', 'players_per_team'].includes(field)) return 1
  if (['payment_method', 'prize_pool'].includes(field)) return 2
  if (['startDate', 'endDate'].includes(field)) return 3
  return LAST_STEP
}

export const BRACKET_OPTIONS = [
  {
    id: 'single_elimination',
    label: 'Single elimination',
    short: 'Single elim',
    help: 'Lose once and you’re out. Fastest to run.',
  },
  {
    id: 'double_elimination',
    label: 'Double elimination',
    short: 'Double elim',
    help: 'Teams drop to a losers bracket after their first loss and are out after a second.',
  },
  {
    id: 'round_robin',
    label: 'Round robin',
    short: 'Round robin',
    help: 'Every team plays every other team. Longest, but nobody goes home early.',
  },
] as const

export const bracketLabel = (id?: string) => BRACKET_OPTIONS.find((b) => b.id === id)?.label

export const FORMATION_LABELS: Record<TeamFormation, string> = {
  teams: 'Teams register',
  captain_pick: 'Captain draft',
  region: 'Region teams',
}

export const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  leet_token: 'LEET',
  coinbase_usdc: 'USDC',
  paypal: 'PayPal',
  free: 'Free',
}

export const PRIZE_UNIT: Record<PaymentMethod, string> = {
  leet_token: 'LEET',
  coinbase_usdc: 'USDC',
  paypal: 'USD',
  free: '',
}

export const formatNumber = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n)

export const formatPrize = (prizePool: string | undefined, method: PaymentMethod) => {
  if (method === 'free') return 'No prize pool'
  const n = parseFloat(prizePool || '')
  if (!Number.isFinite(n) || n <= 0) return '—'
  return method === 'paypal' ? `$${formatNumber(n)}` : `${formatNumber(n)} ${PRIZE_UNIT[method]}`
}

export const formatDate = (value?: string) => {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

/** One-line capacity readout ("16 teams · 80 players" / "Solo signup · teams of 5"). */
export const capacityLine = (formation: TeamFormation, numberOfTeams?: string, teamSize?: string | number) => {
  const size = parseInt(String(teamSize ?? '')) || 0
  if (formation !== 'teams') {
    return size > 0 ? `Solo signup · teams of ${size}` : 'Solo signup · pick a team size'
  }
  const teams = parseInt(numberOfTeams || '') || 0
  if (teams > 0 && size > 0) return `${teams} teams · ${teams * size} players`
  if (teams > 0) return `${teams} teams · pick a team size`
  if (size > 0) return `Teams of ${size} · pick a bracket size`
  return 'Pick a bracket size and team size'
}
